<template>
  <div ref="wrapRef" class="lcs-wrap" @click.stop>
    <!-- Trigger Badge -->
    <button
      ref="badgeRef"
      class="lcs-badge"
      :class="{ 'lcs-badge--selected': currentStage }"
      @click="toggleDropdown"
    >
      <span v-if="currentStage" class="lcs-badge-emoji">{{
        currentStage.emoji
      }}</span>
      <span class="lcs-badge-label">{{
        currentStage
          ? currentStage.name
          : $t('LIFECYCLE.SELECTOR.PLACEHOLDER')
      }}</span>
      <span class="lcs-badge-caret">▾</span>
    </button>

    <!-- Dropdown rendered via fixed positioning to escape overflow:hidden parents -->
    <teleport to="body">
      <transition name="lcs-fade">
        <div
          v-if="isOpen"
          class="lcs-dropdown"
          :style="dropdownStyle"
          @click.stop
        >
          <!-- Search -->
          <div class="lcs-search-wrap">
            <span class="lcs-search-icon">🔍</span>
            <input
              ref="searchInput"
              v-model="query"
              class="lcs-search"
              type="text"
              :placeholder="$t('LIFECYCLE.SELECTOR.SEARCH')"
              @click.stop
            />
            <button
              v-if="query"
              class="lcs-search-clear"
              @click.stop="query = ''"
            >
              ✕
            </button>
          </div>

          <div class="lcs-options-scroll">
            <!-- Lifecycle Stages -->
            <div v-if="filteredLifecycleStages.length" class="lcs-group">
              <p class="lcs-group-label">
                {{ $t('LIFECYCLE.SELECTOR.LIFECYCLE_STAGES') }}
              </p>
              <button
                v-for="stage in filteredLifecycleStages"
                :key="stage.id"
                class="lcs-option"
                :class="{ 'lcs-option--active': isSelected(stage) }"
                @click="selectStage(stage)"
              >
                <span class="lcs-option-emoji">{{ stage.emoji || '🔵' }}</span>
                <span class="lcs-option-name">{{ stage.name }}</span>
                <span v-if="isSelected(stage)" class="lcs-option-check">✓</span>
              </button>
            </div>

            <!-- Lost Stages -->
            <div v-if="filteredLostStages.length" class="lcs-group">
              <p class="lcs-group-label">
                {{ $t('LIFECYCLE.SELECTOR.LOST_STAGES') }}
              </p>
              <button
                v-for="stage in filteredLostStages"
                :key="stage.id"
                class="lcs-option"
                :class="{ 'lcs-option--active': isSelected(stage) }"
                @click="selectStage(stage)"
              >
                <span class="lcs-option-emoji">{{ stage.emoji || '🔴' }}</span>
                <span class="lcs-option-name">{{ stage.name }}</span>
                <span v-if="isSelected(stage)" class="lcs-option-check">✓</span>
              </button>
            </div>

            <!-- Empty state -->
            <div
              v-if="!filteredLifecycleStages.length && !filteredLostStages.length"
              class="lcs-empty"
            >
              {{ $t('LIFECYCLE.SELECTOR.NO_RESULTS') }}
            </div>
          </div>

          <!-- Footer -->
          <div class="lcs-footer">
            <button
              class="lcs-clear"
              :disabled="!currentStage || isSaving"
              @click="clearSelection"
            >
              {{ $t('LIFECYCLE.SELECTOR.CLEAR') }}
            </button>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
export default {
  name: 'ContactLifecycleSelector',

  props: {
    contact: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isOpen: false,
      isSaving: false,
      query: '',
      dropdownTop: 0,
      dropdownLeft: 0,
      localStage: this.contact?.lifecycle_stage || null,
    };
  },

  watch: {
    'contact.lifecycle_stage': {
      immediate: false,
      handler(newVal) {
        // Sync from prop only when we're not mid-save (avoid overwriting optimistic update)
        if (!this.isSaving) {
          this.localStage = newVal || null;
        }
      },
    },
  },

  computed: {
    allStages() {
      return this.$store.getters['lifecycleStages/getAllLifecycleStages'] || [];
    },
    lifecycleStages() {
      return this.allStages
        .filter(s => s.stage_type === 'lifecycle')
        .sort((a, b) => a.position - b.position);
    },
    lostStages() {
      return this.allStages
        .filter(s => s.stage_type === 'lost')
        .sort((a, b) => a.position - b.position);
    },
    filteredLifecycleStages() {
      if (!this.query) return this.lifecycleStages;
      const q = this.query.toLowerCase();
      return this.lifecycleStages.filter(s =>
        s.name.toLowerCase().includes(q)
      );
    },
    filteredLostStages() {
      if (!this.query) return this.lostStages;
      const q = this.query.toLowerCase();
      return this.lostStages.filter(s => s.name.toLowerCase().includes(q));
    },
    currentStage() {
      return this.localStage;
    },
    dropdownStyle() {
      return {
        position: 'fixed',
        top: `${this.dropdownTop}px`,
        left: `${this.dropdownLeft}px`,
        zIndex: 99999,
      };
    },
  },

  mounted() {
    this.$store.dispatch('lifecycleStages/get');
    document.addEventListener('click', this.onOutsideClick);
    window.addEventListener('scroll', this.onScrollOrResize, true);
    window.addEventListener('resize', this.onScrollOrResize);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.onOutsideClick);
    window.removeEventListener('scroll', this.onScrollOrResize, true);
    window.removeEventListener('resize', this.onScrollOrResize);
  },

  methods: {
    updateDropdownPosition() {
      const badge = this.$refs.badgeRef;
      if (!badge) return;
      const rect = badge.getBoundingClientRect();
      this.dropdownTop = rect.bottom + 8;
      this.dropdownLeft = rect.left;
    },

    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.query = '';
        this.updateDropdownPosition();
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },

    onOutsideClick(event) {
      if (
        this.$refs.wrapRef &&
        !this.$refs.wrapRef.contains(event.target) &&
        !event.target.closest('.lcs-dropdown')
      ) {
        this.isOpen = false;
      }
    },

    onScrollOrResize() {
      if (this.isOpen) {
        this.updateDropdownPosition();
      }
    },

    isSelected(stage) {
      return this.currentStage?.id === stage.id;
    },

    async selectStage(stage) {
      if (this.isSelected(stage) || this.isSaving) return;
      this.isOpen = false;
      // Optimistic update — show new stage immediately so UI never reverts
      const previousStage = this.localStage;
      this.localStage = stage;
      this.isSaving = true;
      try {
        await this.$store.dispatch('contacts/update', {
          id: this.contact.id,
          lifecycle_stage_id: stage.id,
        });
      } catch (e) {
        // Rollback if save failed
        this.localStage = previousStage;
      } finally {
        this.isSaving = false;
      }
    },

    async clearSelection() {
      if (!this.currentStage || this.isSaving) return;
      this.isOpen = false;
      // Optimistic update
      const previousStage = this.localStage;
      this.localStage = null;
      this.isSaving = true;
      try {
        await this.$store.dispatch('contacts/update', {
          id: this.contact.id,
          lifecycle_stage_id: null,
        });
      } catch (e) {
        // Rollback if save failed
        this.localStage = previousStage;
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.lcs-wrap {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

/* Badge trigger */
.lcs-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 14px;
  border: 1px solid rgb(var(--slate-6));
  background: rgb(var(--slate-2));
  color: rgb(var(--slate-10));
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  white-space: nowrap;
  line-height: 1.6;
  max-width: 180px;
}

.lcs-badge:hover {
  border-color: #1f93ff;
  color: rgb(var(--slate-12));
  background: rgb(var(--slate-2));
}

.lcs-badge--selected {
  background: rgb(var(--slate-5));
  border-color: #1f93ff;
  color: rgb(var(--blue-9));
}

.lcs-badge-emoji {
  font-size: 1rem;
  line-height: 1;
}

.lcs-badge-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lcs-badge-caret {
  font-size: 0.625rem;
  opacity: 0.55;
  flex-shrink: 0;
}

/* Dropdown */
.lcs-dropdown {
  width: 280px;
  background: rgb(var(--slate-2));
  border: 1px solid rgb(var(--slate-5));
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Transition */
.lcs-fade-enter-active,
.lcs-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.lcs-fade-enter-from,
.lcs-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Search */
.lcs-search-wrap {
  display: flex;
  align-items: center;
  padding: 10px 12px 8px;
  border-bottom: 1px solid rgb(var(--slate-5));
  gap: 8px;
}

.lcs-search-icon {
  font-size: 0.875rem;
  flex-shrink: 0;
  opacity: 0.5;
}

.lcs-search {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: rgb(var(--slate-12));
  font-size: 0.875rem;
  padding: 0;
  caret-color: #1f93ff;
  min-width: 0;
}

.lcs-search::placeholder {
  color: rgb(var(--slate-8));
}

.lcs-search-clear {
  background: none;
  border: none;
  color: rgb(var(--slate-8));
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0 2px;
  transition: color 0.12s;
  flex-shrink: 0;
}

.lcs-search-clear:hover {
  color: rgb(var(--slate-10));
}

/* Scrollable options area */
.lcs-options-scroll {
  max-height: 260px;
  overflow-y: auto;
  overflow-x: hidden;
}

.lcs-options-scroll::-webkit-scrollbar {
  width: 4px;
}

.lcs-options-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.lcs-options-scroll::-webkit-scrollbar-thumb {
  background: rgb(var(--slate-5));
  border-radius: 2px;
}

/* Group */
.lcs-group {
  padding: 6px 0;
}

.lcs-group-label {
  padding: 4px 14px 3px;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgb(var(--slate-8));
  margin: 0;
}

/* Option */
.lcs-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 14px;
  background: none;
  border: none;
  color: rgb(var(--slate-11));
  font-size: 0.875rem;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
}

.lcs-option:hover {
  background: rgb(var(--slate-4));
  color: rgb(var(--slate-12));
}

.lcs-option--active {
  color: rgb(var(--blue-9));
  background: rgb(var(--slate-2));
}

.lcs-option-emoji {
  font-size: 1.1rem;
  flex-shrink: 0;
  line-height: 1;
}

.lcs-option-name {
  flex: 1;
  font-weight: 500;
}

.lcs-option-check {
  color: #1f93ff;
  font-size: 0.875rem;
  font-weight: 800;
  flex-shrink: 0;
}

/* Empty state */
.lcs-empty {
  padding: 20px 14px;
  color: rgb(var(--slate-8));
  font-size: 0.8125rem;
  text-align: center;
}

/* Footer */
.lcs-footer {
  padding: 8px 10px;
  border-top: 1px solid rgb(var(--slate-5));
  display: flex;
  justify-content: flex-end;
}

.lcs-clear {
  background: none;
  border: 1px solid rgb(var(--slate-6));
  border-radius: 6px;
  color: rgb(var(--slate-9));
  font-size: 0.75rem;
  padding: 5px 12px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}

.lcs-clear:hover:not(:disabled) {
  color: rgb(var(--slate-10));
  border-color: rgb(var(--slate-8));
}

.lcs-clear:disabled {
  opacity: 0.35;
  cursor: default;
}
</style>
